import axiosInstance from '../axios.config';
import {
  ApiKeyDTO,
  ApiKeyCreateResponse,
  CreateApiKeyRequest,
  ApiKeyStatsDTO,
  WebhookDTO,
  WebhookLogDTO,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  TestWebhookRequest,
  TestWebhookResponse,
  WebhookStatsDTO,
  WebhookStatus,
} from '../../types/developer';

// ============================================================================
// Types
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}

// ============================================================================
// API Keys Service
// ============================================================================

export const apiKeyService = {
  // List API Keys
  list: async (params: {
    organizationId: string;
    active?: boolean;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedResponse<ApiKeyDTO>>> => {
    try {
      const response = await axiosInstance.get('/developer/api-keys', {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch API keys',
      };
    }
  },

  // Get single API Key
  get: async (keyId: string): Promise<ApiResponse<ApiKeyDTO>> => {
    try {
      const response = await axiosInstance.get(`/developer/api-keys/${keyId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch API key',
      };
    }
  },

  // Create API Key (returns secret only once)
  create: async (
    userId: string,
    organizationId: string,
    request: CreateApiKeyRequest
  ): Promise<ApiResponse<ApiKeyCreateResponse>> => {
    try {
      const response = await axiosInstance.post('/developer/api-keys', request, {
        params: { userId, organizationId },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create API key',
      };
    }
  },

  // Revoke API Key
  revoke: async (keyId: string): Promise<ApiResponse<ApiKeyDTO>> => {
    try {
      const response = await axiosInstance.post(`/developer/api-keys/${keyId}/revoke`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to revoke API key',
      };
    }
  },

  // Regenerate API Key (returns new secret)
  regenerate: async (keyId: string): Promise<ApiResponse<ApiKeyCreateResponse>> => {
    try {
      const response = await axiosInstance.post(`/developer/api-keys/${keyId}/regenerate`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to regenerate API key',
      };
    }
  },

  // Delete API Key
  delete: async (keyId: string): Promise<ApiResponse<void>> => {
    try {
      await axiosInstance.delete(`/developer/api-keys/${keyId}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete API key',
      };
    }
  },

  // Get API Key Statistics
  getStats: async (organizationId: string): Promise<ApiResponse<ApiKeyStatsDTO>> => {
    try {
      const response = await axiosInstance.get('/developer/api-keys/stats', {
        params: { organizationId },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch API key statistics',
      };
    }
  },
};

// ============================================================================
// Webhook create — the backend contract
// ============================================================================

/**
 * The organisation the request acts on: what the page chose, else the one
 * axios.config puts in X-Organization-Id (same localStorage key, same
 * 'synthetic-org' exclusion).
 */
const currentOrganizationIdFromStorage = (): string | undefined => {
  try {
    const id = localStorage.getItem('currentOrganizationId');
    return id && id !== 'synthetic-org' ? id : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Query params for POST /developer/webhooks. The backend reads the org and the
 * creator from here, NOT from the body — sending neither was why every create
 * from this page failed. Empty values are omitted (the backend then defaults the
 * creator to the caller and the org to X-Organization-Id).
 */
export const webhookCreateParams = (
  request: CreateWebhookRequest,
  context: { organizationId?: string; userId?: string } = {}
): { organizationId?: string; userId?: string } => {
  const organizationId =
    context.organizationId || request.organizationId || currentOrganizationIdFromStorage();
  const params: { organizationId?: string; userId?: string } = {};
  if (organizationId) params.organizationId = organizationId;
  if (context.userId) params.userId = context.userId;
  return params;
};

/** Portal event names → the backend's (WebhookEventType.eventName). Unknown ones pass through lower-cased. */
const BACKEND_EVENT: Partial<Record<string, string>> = {
  CALL_INITIATED: 'call_start',
  CALL_ANSWERED: 'call_answered',
  CALL_COMPLETED: 'call_end',
  CALL_FAILED: 'call_failed',
  CALL_TRANSFERRED: 'call_transfer',
  WHATSAPP_MESSAGE_RECEIVED: 'whatsapp_message_received',
  WHATSAPP_MESSAGE_SENT: 'whatsapp_message_status_updated',
  WHATSAPP_MESSAGE_DELIVERED: 'whatsapp_message_status_updated',
  WHATSAPP_MESSAGE_READ: 'whatsapp_message_status_updated',
  WHATSAPP_MESSAGE_FAILED: 'whatsapp_message_status_updated',
  CAMPAIGN_STARTED: 'whatsapp_campaign_started',
  CAMPAIGN_COMPLETED: 'whatsapp_campaign_completed',
  CAMPAIGN_FAILED: 'whatsapp_campaign_failed',
};

/**
 * The body the backend's CreateWebhookRequest binds: url, httpMethod,
 * webhookType (required — @NotBlank), eventTypes, customHeaders, and timings in
 * milliseconds. The form's own names (method, events, headers, *Seconds) were
 * silently dropped by Jackson, and the missing webhookType alone failed
 * validation.
 *
 * webhookType picks which delivery path reads the config: 'whatsapp' when every
 * selected event is a WhatsApp/campaign one, otherwise the voice path
 * ('yamurai-voice').
 */
export const toBackendCreateWebhook = (request: CreateWebhookRequest): Record<string, unknown> => {
  const events = (request.events || []).map((e) => BACKEND_EVENT[e] || String(e).toLowerCase());
  const whatsappOnly = events.length > 0 && events.every((e) => e.startsWith('whatsapp_'));
  const body: Record<string, unknown> = {
    name: request.name,
    url: request.url,
    httpMethod: request.method || request.httpMethod || 'POST',
    webhookType: whatsappOnly ? 'whatsapp' : 'yamurai-voice',
    eventTypes: Array.from(new Set(events)),
    customHeaders: request.headers || request.customHeaders || {},
    description: request.description,
  };
  if (request.maxRetries !== undefined) body.maxRetries = request.retryEnabled === false ? 0 : request.maxRetries;
  if (request.retryDelaySeconds !== undefined) body.retryDelayMs = request.retryDelaySeconds * 1000;
  if (request.timeoutSeconds !== undefined) body.readTimeout = request.timeoutSeconds * 1000;
  return body;
};

// ============================================================================
// Webhooks Service
// ============================================================================

export const webhookService = {
  // List Webhooks
  list: async (params: {
    organizationId?: string;
    status?: string;
    enabled?: boolean;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedResponse<WebhookDTO>>> => {
    try {
      const response = await axiosInstance.get('/developer/webhooks', {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch webhooks',
      };
    }
  },

  // Get single Webhook
  get: async (webhookId: string): Promise<ApiResponse<WebhookDTO>> => {
    try {
      const response = await axiosInstance.get(`/developer/webhooks/${webhookId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch webhook',
      };
    }
  },

  // Create Webhook
  create: async (
    request: CreateWebhookRequest,
    context: { organizationId?: string; userId?: string } = {}
  ): Promise<ApiResponse<WebhookDTO>> => {
    try {
      const response = await axiosInstance.post(
        '/developer/webhooks',
        toBackendCreateWebhook(request),
        { params: webhookCreateParams(request, context) }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create webhook',
      };
    }
  },

  // Update Webhook
  update: async (webhookId: string, request: UpdateWebhookRequest): Promise<ApiResponse<WebhookDTO>> => {
    try {
      const response = await axiosInstance.put(`/developer/webhooks/${webhookId}`, request);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update webhook',
      };
    }
  },

  // Delete Webhook
  delete: async (webhookId: string): Promise<ApiResponse<void>> => {
    try {
      await axiosInstance.delete(`/developer/webhooks/${webhookId}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete webhook',
      };
    }
  },

  // Enable Webhook
  enable: async (webhookId: string): Promise<ApiResponse<WebhookDTO>> => {
    try {
      const response = await axiosInstance.post(`/developer/webhooks/${webhookId}/enable`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to enable webhook',
      };
    }
  },

  // Disable Webhook
  disable: async (webhookId: string): Promise<ApiResponse<WebhookDTO>> => {
    try {
      const response = await axiosInstance.post(`/developer/webhooks/${webhookId}/disable`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to disable webhook',
      };
    }
  },

  // Test Webhook
  test: async (webhookId: string, request: TestWebhookRequest): Promise<ApiResponse<TestWebhookResponse>> => {
    try {
      const response = await axiosInstance.post(`/developer/webhooks/${webhookId}/test`, request);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to test webhook',
      };
    }
  },

  // Get Webhook Delivery Logs
  getLogs: async (
    webhookId: string,
    params?: {
      status?: WebhookStatus;
      page?: number;
      size?: number;
    }
  ): Promise<ApiResponse<PaginatedResponse<WebhookLogDTO>>> => {
    try {
      const response = await axiosInstance.get(`/developer/webhooks/${webhookId}/logs`, {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch webhook logs',
      };
    }
  },

  // Retry failed webhook delivery
  retry: async (webhookId: string, logId: string): Promise<ApiResponse<WebhookLogDTO>> => {
    try {
      const response = await axiosInstance.post(`/developer/webhooks/logs/${logId}/retry`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to retry webhook delivery',
      };
    }
  },

  // Get Webhook Statistics
  getStats: async (organizationId?: string): Promise<ApiResponse<WebhookStatsDTO>> => {
    try {
      const response = await axiosInstance.get('/developer/webhooks/stats', {
        params: organizationId ? { organizationId } : undefined,
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch webhook statistics',
      };
    }
  },
};

export default {
  apiKeys: apiKeyService,
  webhooks: webhookService,
};
